import { useTranslation } from "react-i18next";
import TrendChart from "../../../shared/TrendChart/TrendChart";
import { KpiSeries, PerformanceMetricsLabel } from "../../../shared/types";
import "./MetricCard.scss";
import defaultContent from "../../../../../content/downlineProfile";
import { IconTrendArrow } from "../../../../../common/icons/IconTrendArrow/IconTrendArrow";
import { getShortPeriodFormat } from "../../../../../common/util/period";
import { storedLocale } from "../../../../Authentication/util";
import LeafRectangleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { DECIMAL_KPIS } from "../../../shared/utils";
interface MetricCardProps {
  series?: KpiSeries | null;
  endPeriod?: number;
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  searchId?: string;
}

interface TrendArrowProps {
  direction?: string;
  difference?: string;
  sorted?: KpiSeries | null;
}

const TrendArrow = ({ direction, sorted, difference }: TrendArrowProps) => {
  const { t } = useTranslation(["downlineProfile"]);
  if (direction === "unchanged") {
    return (
      <>
        <span className="trend-arrow-container trend-arrow-container--gray">{difference}</span>
      </>
    );
  }
  return (
    <>
      <span
        className={`trend-arrow-container ${direction === "up" ? "trend-arrow-container--green" : "trend-arrow-container--red"}`}
      >
        <IconTrendArrow direction={direction as "up" | "down"} color={`${direction === "up" ? "green" : "red"}`} />
        <p>
          {difference}{" "}
          {DECIMAL_KPIS.has(sorted?.kpi ?? "") &&
            t(
              `performanceMetrics.${sorted?.kpi}Abbr`,
              defaultContent.performanceMetrics[`${sorted?.kpi}Abbr` as PerformanceMetricsLabel],
            )}
        </p>
      </span>
    </>
  );
};
const MetricCard = ({ series, endPeriod, isLoading, isError, searchId, onRetry }: MetricCardProps) => {
  const { t } = useTranslation(["downlineProfile"]);
  const sorted = series
    ? {
        ...series,
        years: [...(series.years ?? [])].sort((a, b) => a.performanceYear - b.performanceYear),
      }
    : null;
  const prevYearPeriod = String(Number(endPeriod) - 100);
  return (
    <>
      <div className={`dp-metric-card ${isError && "dp-metric-card--error"}`}>
        {!isError && (
          <div className="dp-metric-card__header">
            <div className="dp-metric-card__header__label">
              <LeafRectangleLoader isLoading={isLoading} width="8rem" height="1.5rem">
                <p>{`${t(`performanceMetrics.${sorted?.kpi ?? ""}`, defaultContent.performanceMetrics[sorted?.kpi as PerformanceMetricsLabel])}`}</p>
              </LeafRectangleLoader>
              {series?.years !== null && (
                <div>
                  <LeafRectangleLoader isLoading={isLoading} width="4rem" height="2.25rem">
                    <p>{`${sorted?.years.at(-1)?.points.at(-1)?.value.formatted}`}</p>
                  </LeafRectangleLoader>
                  <LeafRectangleLoader isLoading={isLoading} width="10rem" height="1.5rem">
                    <p>
                      {t("performanceMetrics.lastClosedMonth", defaultContent.performanceMetrics.lastClosedMonth, {
                        date: getShortPeriodFormat(`${endPeriod ?? ""}`, storedLocale()),
                      })}
                    </p>
                  </LeafRectangleLoader>
                </div>
              )}
            </div>
            {series?.years !== null && (
              <div className="dp-metric-card__header__info">
                <LeafRectangleLoader isLoading={isLoading} width="5rem" height="1.5rem">
                  <TrendArrow direction={series?.yoy} sorted={sorted} difference={sorted?.yoyDifference} />
                </LeafRectangleLoader>
                <LeafRectangleLoader isLoading={isLoading} width="6rem" height="1.5rem">
                  <p>
                    {t("performanceMetrics.versusDate", defaultContent.performanceMetrics.versusDate, {
                      date: getShortPeriodFormat(`${prevYearPeriod}`, storedLocale()),
                    })}
                  </p>
                </LeafRectangleLoader>
              </div>
            )}
          </div>
        )}
        <TrendChart
          series={sorted}
          endPeriod={endPeriod}
          isLoading={isLoading}
          isError={isError}
          onRetry={onRetry}
          searchId={searchId}
        />
      </div>
    </>
  );
};

export default MetricCard;
